const DRAFT_PREFIX = 'hd-draft:';
const DRAFT_TTL_MS = 8 * 60 * 60 * 1000;

const LEGACY_LOCAL_STORAGE_PREFIXES = [
    'message-draft-',
    'internal-task-draft-',
    'journal-note-draft-',
    'clinical-message-draft-',
    'referral-draft-',
];

type StoredDraft = { savedAt: number; value: string };

let ownerId: number | undefined;

const getOwnerPrefix = () => `${DRAFT_PREFIX}${ownerId}:`;

const getKeys = (storage: Storage) =>
    Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
        (key): key is string => key !== null,
    );

const parseDraft = (raw: string | null): StoredDraft | null => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return typeof parsed?.savedAt === 'number' &&
            typeof parsed?.value === 'string'
            ? parsed
            : null;
    } catch {
        return null;
    }
};

const isExpired = (draft: StoredDraft) =>
    Date.now() - draft.savedAt > DRAFT_TTL_MS;

const removeLegacyDrafts = () => {
    getKeys(localStorage)
        .filter((key) =>
            LEGACY_LOCAL_STORAGE_PREFIXES.some((prefix) =>
                key.startsWith(prefix),
            ),
        )
        .forEach((key) => localStorage.removeItem(key));
};

/**
 * Binds drafts to the signed-in user and purges drafts of other users,
 * expired drafts and legacy localStorage drafts. Drafts are unavailable
 * until an owner is set.
 */
export const setDraftOwner = (userId: number): void => {
    ownerId = userId;

    getKeys(localStorage)
        .filter((key) => key.startsWith(DRAFT_PREFIX))
        .forEach((key) => {
            const draft = key.startsWith(getOwnerPrefix())
                ? parseDraft(localStorage.getItem(key))
                : null;
            if (!draft || isExpired(draft)) {
                localStorage.removeItem(key);
            }
        });

    removeLegacyDrafts();
};

export const getDraft = (key: string): string | null => {
    if (ownerId === undefined) return null;

    const storageKey = `${getOwnerPrefix()}${key}`;
    const draft = parseDraft(localStorage.getItem(storageKey));
    if (!draft || isExpired(draft)) {
        localStorage.removeItem(storageKey);
        return null;
    }
    return draft.value;
};

export const setDraft = (key: string, value: string): void => {
    if (ownerId === undefined) return;

    const draft: StoredDraft = { savedAt: Date.now(), value };
    localStorage.setItem(`${getOwnerPrefix()}${key}`, JSON.stringify(draft));
};

export const removeDraft = (key: string): void => {
    if (ownerId === undefined) return;

    localStorage.removeItem(`${getOwnerPrefix()}${key}`);
};

export const clearAllDrafts = (): void => {
    getKeys(localStorage)
        .filter((key) => key.startsWith(DRAFT_PREFIX))
        .forEach((key) => localStorage.removeItem(key));

    removeLegacyDrafts();
};
